/**
 * 購物金管理 API 服務
 * 使用 RTK Query 封裝購物金相關的 API 調用
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ConstantUtil } from '@/utils/ConstantUtil';
import {
  IIssueShoppingCreditRequest,
  IIssueShoppingCreditResponse,
  IShoppingCreditUsageParams,
  IShoppingCreditUsageResponse,
  IShoppingCreditReportParams,
  IShoppingCreditReportResponse,
  IMemberSearchParams,
  IMemberSearchResponse,
} from '@/types/shoppingCredit';

/**
 * 購物金 API 服務
 */
export const shoppingCreditApi = createApi({
  reducerPath: 'shoppingCreditApi',
  baseQuery: fetchBaseQuery({
    baseUrl: ConstantUtil.API_BASE_URL,
    timeout: ConstantUtil.API_TIMEOUT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem(ConstantUtil.STORAGE_USER_TOKEN);
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['ShoppingCredit', 'ShoppingCreditUsage', 'ShoppingCreditReport'],
  endpoints: (builder) => ({
    /**
     * 發放購物金
     */
    issueShoppingCredit: builder.mutation<
      IIssueShoppingCreditResponse,
      IIssueShoppingCreditRequest
    >({
      query: (data) => ({
        url: ConstantUtil.API_SHOPPING_CREDIT_ISSUE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ShoppingCredit', 'ShoppingCreditReport'],
    }),

    /**
     * 查詢購物金使用紀錄
     */
    getShoppingCreditUsage: builder.query<
      IShoppingCreditUsageResponse,
      IShoppingCreditUsageParams
    >({
      query: (params) => ({
        url: ConstantUtil.API_SHOPPING_CREDIT_USAGE,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.memberId && { memberId: params.memberId }),
          ...(params.memberName && { memberName: params.memberName }),
          ...(params.orderNumber && { orderNumber: params.orderNumber }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
        },
      }),
      providesTags: ['ShoppingCreditUsage'],
    }),

    /**
     * 查詢購物金報表
     */
    getShoppingCreditReports: builder.query<
      IShoppingCreditReportResponse,
      IShoppingCreditReportParams
    >({
      query: (params) => ({
        url: ConstantUtil.API_SHOPPING_CREDIT_REPORTS,
        method: 'GET',
        params: {
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
        },
      }),
      providesTags: ['ShoppingCreditReport'],
    }),

    /**
     * 搜尋會員（用於發放購物金時選擇會員）
     */
    searchMembers: builder.query<IMemberSearchResponse, IMemberSearchParams>({
      query: (params) => ({
        url: ConstantUtil.API_SHOPPING_CREDIT_MEMBERS,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 10,
          ...(params.keyword && { keyword: params.keyword }),
        },
      }),
    }),
  }),
});

export const {
  useIssueShoppingCreditMutation,
  useGetShoppingCreditUsageQuery,
  useGetShoppingCreditReportsQuery,
  useSearchMembersQuery,
} = shoppingCreditApi;
